import auth0 from 'auth0-js';
import Bluebird from 'bluebird';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import { generatePath, matchPath } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App as CapacitorApp } from '@capacitor/app';
import { store } from '../store/index';
import * as authActions from '../actions/auth';
import * as appActions from '../actions/app';
import * as usersActions from '../actions/users';
import * as sitesActions from '../actions/sites';
import * as uiPersistedActions from '../actions/uiPersisted';
import * as authApi from '../api/auth';
import * as usersModel from '../models/users';
import snackbarHelper from '../helpers/snackbarHelper';
import routes from '../routes/index';
import { resetUI } from '../actions/uiPersisted';
import * as userRegistrationsApi from '../api/userRegistrations';
import { Roles } from '../models/roles';
import errorMessages from '../assets/errorMessages';
import history from '../history';
import { setFABActions } from '../helpers/fab';

const customClaimsUrl = 'https://app.foodrescue.us/claims';
const NATIVE_CALLBACK_URL = 'us.foodrescue.foodrescueus://frus.auth0.com/capacitor/us.foodrescue.foodrescueus/callback';

let authPopup;

// --- Native Auth0 client (PKCE, system browser) ---
// Used only when Capacitor.isNativePlatform() is true.
// Initialized lazily on first use so the web build is unaffected.
let _nativeClient = null;
let _nativeCallbackResolve = null;
let _nativeCallbackReject = null;

// Register once at module level — avoids duplicate listeners across logout/re-login cycles.
// Handles both login callbacks (code + state) and logout redirects (Browser.close).
if (Capacitor.isNativePlatform()) {
  CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
    // Auth0 login callbacks and logout redirects always return on the custom
    // scheme (NATIVE_CALLBACK_URL). Universal Links / App Links arrive on the
    // https web domain and are handled in services/deepLinks.js — ignore them
    // here so we don't close an unrelated browser or swallow a deep link.
    if (!url?.startsWith('us.foodrescue.foodrescueus://')) {
      return;
    }

    if (url.includes('state') && (url.includes('code') || url.includes('error'))) {
      if (_nativeCallbackResolve) {
        const resolve = _nativeCallbackResolve;
        _nativeCallbackResolve = null;
        _nativeCallbackReject = null;
        resolve(url);
      }
    }
    // Required on Android to dismiss Chrome Custom Tabs after both login and logout redirects.
    await Browser.close();
  });
}

const _initNativeAuth0 = async () => {
  if (_nativeClient) {
    return _nativeClient;
  }
  const { createAuth0Client } = await import('@auth0/auth0-spa-js');
  _nativeClient = await createAuth0Client({
    domain: 'frus.auth0.com',
    clientId: process.env.REACT_APP_AUTH0_NATIVE_CLIENT_ID,
    authorizationParams: {
      redirect_uri: NATIVE_CALLBACK_URL,
      audience: 'https://api.foodrescue.us',
    },
    useRefreshTokens: true,
    cacheLocation: 'localstorage',
    // On Android, Chrome Custom Tabs can kill the WebView process while the browser is open,
    // wiping sessionStorage. Cookies survive the relaunch via the callback URL scheme.
    // On iOS, the WKWebView stays alive during SFSafariViewController, so sessionStorage
    // works fine. Cookies must NOT be used on iOS: the app runs under capacitor://localhost
    // (not HTTPS), so cookies with the Secure flag are silently dropped, leaving no
    // transaction for handleRedirectCallback to find → "missing_transaction" error.
    useCookiesForTransactions: Capacitor.getPlatform() === 'android',
  });
  return _nativeClient;
};

const auth = new auth0.WebAuth({
  clientID: process.env.REACT_APP_AUTH0_CLIENT_ID,
  domain: 'frus.auth0.com',
  responseType: 'token id_token',
  audience: 'https://api.foodrescue.us',
  redirectUri: `${window.location.origin.toString()}/callback`,
  scope: 'openid profile email'
});

export const getIdTokenPayloadCustomClaim = (idTokenPayload, claimName) => {
  return idTokenPayload[`${customClaimsUrl}/${claimName}`];
};

export const handleAuthenticationPopupCallback = () => auth.popup.callback(); // goes to authorizeWithGoogle callback

export const setFABActionsForCurrentlyLoggedInUser = fabContext => {
  const { app: { loggedInUser } } = store.getState();

  // let's trigger set floating action button actions only for users who completed registration process
  if (!loggedInUser || !loggedInUser.completed_registration) {
    return false;
  }

  return setFABActions(loggedInUser, history, fabContext);
};

// return promise from auth0 call (called from LoginPage.handleLoginWithGoogle()
export const authorizeWithGoogle = async (options, fabContext = false) => {
  if (Capacitor.isNativePlatform()) {
    // Native: Authorization Code + PKCE via system browser (no popup)
    try {
      store.dispatch(authActions.setLoggingIn(true));
      const client = await _initNativeAuth0();

      const callbackUrlPromise = new Promise((resolve, reject) => {
        _nativeCallbackResolve = resolve;
        _nativeCallbackReject = reject;
        setTimeout(() => {
          if (_nativeCallbackReject) {
            _nativeCallbackResolve = null;
            _nativeCallbackReject = null;
            reject(new Error('Authentication timed out'));
          }
        }, 5 * 60 * 1000);
      });

      await client.loginWithRedirect({
        authorizationParams: { connection: 'google-oauth2' },
        openUrl: (url) => Browser.open({ url, windowName: '_self' }),
      });

      const callbackUrl = await callbackUrlPromise;
      await client.handleRedirectCallback(callbackUrl);

      const accessToken = await client.getTokenSilently();
      const idTokenClaims = await client.getIdTokenClaims();
      const idToken = idTokenClaims.__raw;

      const res = await authApi.loginViaSocial(accessToken, idToken);
      const json = await res.json();
      await handleAuthentication(history, json.data);

      if (fabContext) {
        setFABActionsForCurrentlyLoggedInUser(fabContext);
      }
    } catch (err) {
      if (err.code === errorMessages.ERR_SYSTEM_NOT_AVAILABLE.code) {
        return history.push(routes.systemNotAvailable);
      }
      console.error('authorizeWithGoogle (native) error caught: ', err);
    } finally {
      store.dispatch(authActions.setLoggingIn(false));
    }
    return;
  }

  // Web: existing popup flow — unchanged
  // force close auth popup - to fix "minimizing" behaviour in PWA
  if (authPopup) {
    authPopup.close();
  }

  authPopup = auth.popup.authorize(
    {
      ...options,
      prompt: 'login',
      connection: 'google-oauth2',
      redirectUri: `${window.location.origin.toString()}/oauth/callback`,
    },
    (error, authResults) => {
      if (error) {
        if (error.original !== 'User closed the popup window' && error.hasOwnProperty('errorDescription')) {
          snackbarHelper.error(error.errorDescription);
        }

        return logout();
      }

      return Bluebird
        .try(() => store.dispatch(authActions.setLoggingIn(true)))
        .then(() => authApi.loginViaSocial(authResults.accessToken, authResults.idToken))
        .then(res => res.json())
        .then(res => res.data)
        .then(data => handleAuthentication(history, data))
        .then(() => {
          if (!fabContext) {
            return false;
          }

          return setFABActionsForCurrentlyLoggedInUser(fabContext);
        })
        .catch(err => {
          if (err.code === errorMessages.ERR_SYSTEM_NOT_AVAILABLE.code) {
            return history.push(routes.systemNotAvailable);
          }

          console.error('authorizeWithGoogle error caught: ', err);
        })
        .finally(() => store.dispatch(authActions.setLoggingIn(false)));
    }
  );
};

// return promise from auth0 call (called from LoginPage.handleLoginWithApple()
export const authorizeWithApple = async (options, fabContext = false) => {
  if (Capacitor.isNativePlatform()) {
    try {
      store.dispatch(authActions.setLoggingIn(true));
      const client = await _initNativeAuth0();

      const callbackUrlPromise = new Promise((resolve, reject) => {
        _nativeCallbackResolve = resolve;
        _nativeCallbackReject = reject;
        setTimeout(() => {
          if (_nativeCallbackReject) {
            _nativeCallbackResolve = null;
            _nativeCallbackReject = null;
            reject(new Error('Authentication timed out'));
          }
        }, 5 * 60 * 1000);
      });

      await client.loginWithRedirect({
        authorizationParams: { connection: 'apple' },
        openUrl: (url) => Browser.open({ url, windowName: '_self' }),
      });

      const callbackUrl = await callbackUrlPromise;
      await client.handleRedirectCallback(callbackUrl);

      const accessToken = await client.getTokenSilently();
      const idTokenClaims = await client.getIdTokenClaims();
      const idToken = idTokenClaims.__raw;

      const res = await authApi.loginViaSocial(accessToken, idToken);
      const json = await res.json();
      await handleAuthentication(history, json.data);

      if (fabContext) {
        setFABActionsForCurrentlyLoggedInUser(fabContext);
      }
    } catch (err) {
      if (err.code === errorMessages.ERR_SYSTEM_NOT_AVAILABLE.code) {
        return history.push(routes.systemNotAvailable);
      }
      console.error('authorizeWithApple (native) error caught: ', err);
    } finally {
      store.dispatch(authActions.setLoggingIn(false));
    }
    return;
  }

  // Web: popup flow
  if (authPopup) {
    authPopup.close();
  }

  authPopup = auth.popup.authorize(
    {
      ...options,
      prompt: 'login',
      connection: 'apple',
      redirectUri: `${window.location.origin.toString()}/oauth/callback`,
    },
    (error, authResults) => {
      if (error) {
        if (error.original !== 'User closed the popup window' && error.hasOwnProperty('errorDescription')) {
          snackbarHelper.error(error.errorDescription);
        }

        return logout();
      }

      return Bluebird
        .try(() => store.dispatch(authActions.setLoggingIn(true)))
        .then(() => authApi.loginViaSocial(authResults.accessToken, authResults.idToken))
        .then(res => res.json())
        .then(res => res.data)
        .then(data => handleAuthentication(history, data))
        .then(() => {
          if (!fabContext) {
            return false;
          }

          return setFABActionsForCurrentlyLoggedInUser(fabContext);
        })
        .catch(err => {
          if (err.code === errorMessages.ERR_SYSTEM_NOT_AVAILABLE.code) {
            return history.push(routes.systemNotAvailable);
          }

          console.error('authorizeWithApple error caught: ', err);
        })
        .finally(() => store.dispatch(authActions.setLoggingIn(false)));
    }
  );
};

export const userInfo = (accessToken, callback) => auth.client.userInfo(accessToken, callback);

// called from Login page onSubmit() with user return from our API (getUserByEmail)
export const handleAuthentication = async (history, authResults) => {
  if (authResults && authResults.access_token && authResults.id_token) {
    const { user } = authResults;

    console.log('handleAuthentication: ', {user} );
    // @todo why are the parms user.id and user.role_assignments and not just
    // the user {} ?
    if (user && user.completed_registration) {
      return login(
        authResults.access_token,
        authResults.id_token,
        authResults.id_token_payload,
        user.id,
        user.role_assignments,
        history,
        false,
      );
    }

    let idTokenPayloadData = authResults.id_token_payload;

    if (user && user.invitation_hash) {
      idTokenPayloadData = {
        ...idTokenPayloadData,
        id: user.id,
        invitation_hash: user.invitation_hash,
      }
    }

    store.dispatch(authActions.setAuthenticatedUser());
    store.dispatch(authActions.setImpersonatedUser());
    store.dispatch(authActions.setUserRegistration(authResults.access_token, authResults.id_token, idTokenPayloadData));

    // here's the redirect if an email+passowrd authentication is successful,
    // the user has an incomplete registration
    // the registration complete path is hidden in login()
    const userRegistrationType = await (async () => {
      const res = await userRegistrationsApi.getUserRegistrationByEmail(
        idTokenPayloadData.email,
        authResults.access_token
      );
      const json = await res.json();

      if (!json.data.length) {
        return 'rescuer';
      }

      return json.data[0].type;
    })();

    history.push(generatePath(routes.completeRegistrationNew));

  } else {
    snackbarHelper.error('Error occurred during authentication process.');

    return logout();
  }
};

export const login = (accessToken, idToken, idTokenPayload, userId, roleAssignments, history, firstVisit) => {
  console.log('login: ', { roleAssignments } );
  store.dispatch(authActions.setUserRegistration());

  store.dispatch(
    authActions.setAuthenticatedUser(
      accessToken,
      idToken,
      usersModel.apiToStore({
        ...idTokenPayload,
        ...{
          id: userId,
          role_assignments: roleAssignments,
        }
      })
    )
  );

  // fetchAndSetLoggedInUser requires fetchAndSetLoggedInUser to be called first

  // and, finally, we see the redirect for a successful email+password login
  // for a user with a completed registration
  return Bluebird
    .try(() => setDefaultSiteAndSites(roleAssignments))
    .then(() => store.dispatch(usersActions.fetchAndSetLoggedInUser(userId)))
    .then(() => {
      if (firstVisit) {
        return history.push(routes.welcomePage);
      }

      return history.push(routes.index);
    });
};

export const createProfile = (userRegistration, history, showWelcomePage = true) =>
  Bluebird.try(() =>
    userRegistrationsApi.updateUserRegistration(
      userRegistration.data.id,
      {
        create_profile: true,
      },
      userRegistration.accessToken,
    ),
  )
    .then(res => res.json())
    .then(res =>
      login(
        userRegistration.accessToken,
        userRegistration.idToken,
        userRegistration.idTokenPayload,
        res.id,
        res.role_assignments,
        history,
        showWelcomePage
      )
    );

export const createProfileWithoutLogin = (userRegistration) =>
  Bluebird.try(() =>
    userRegistrationsApi.updateUserRegistration(
      userRegistration.data.id,
      {
        create_profile: true,
      },
      userRegistration.accessToken,
    ),
  )
    .then(res => res.json());

export const refreshNativeTokens = async () => {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }
  try {
    const client = await _initNativeAuth0();
    const accessToken = await client.getTokenSilently();
    const idTokenClaims = await client.getIdTokenClaims();
    store.dispatch(
      authActions.setAuthenticatedUser(
        accessToken,
        idTokenClaims.__raw,
        store.getState().app.idTokenPayload,
      )
    );
    return accessToken;
  } catch (e) {
    logout();
    return null;
  }
};

export const logout = () => {
  if (Capacitor.isNativePlatform() && _nativeClient) {
    // Open system browser to Auth0 logout endpoint to clear the server-side session.
    // appUrlOpen will fire when Auth0 redirects back, closing the browser.
    _nativeClient.logout({
      logoutParams: { returnTo: NATIVE_CALLBACK_URL },
      openUrl: (url) => Browser.open({ url, windowName: '_self' }),
    }).catch(() => {});
    _nativeClient = null;
  }
  store.dispatch(authActions.resetStateToDefault());
  store.dispatch(resetUI());
  store.dispatch(uiPersistedActions.closeDrawer());
};

export const hasRoleInCurrentlySelectedSite = (user, role) => {
  const { app } = store.getState();

  if (!user || !user.role_assignments || !app.site) {
    return false;
  }

  return getUserRolesInCurrentlySelectedSite(user)
    .filter(roleAssignment => roleAssignment.role_name === role)
    .length > 0;
};

export const hasAnyRoleInCurrentlySelectedSite = (user, roles) => {
  return roles.some(role => hasRoleInCurrentlySelectedSite(user, role));
};

export const hasAnyRoleInSite = (user, siteId, roles) => {
  if (!user || !user.role_assignments) {
    return false;
  }

  const userRoles = getUserRolesInSite(user, siteId);

  return roles.some(role => userRoles.find(ra => ra.role_name === role));
};

export const hasRoleInAnySite = (user, role) => {
  if(!user || !user.role_assignments) {
    return false;
  }

  return getUserSitesByRole(user.role_assignments, role).length > 0
}

export const hasOnlyRoleInCurrentlySelectedSite = (user, role) => {
  if (user === undefined || user.role_assignments === undefined) {
    return false;
  }

  const roles = getUserRolesInCurrentlySelectedSite(user);

  return roles.length === 1 && hasRoleInCurrentlySelectedSite(user, role);
};

export const getUserRolesInSite = (user, siteId) => _getGroupedGlobalAndNonGlobalRoles(user.role_assignments, siteId);

export const getUserRolesInCurrentlySelectedSite = user => {
  const { app } = store.getState();

  if (!app.site) {
    return [];
  }

  return _getGroupedGlobalAndNonGlobalRoles(user.role_assignments, app.site.id);
};

export const currentlyLoggedInOrImpersonatingUserHasRoleInCurrentlySelectedSite = role => {
  return hasRoleInCurrentlySelectedSite(getCurrentlyLoggedInOrImpersonatingUser(), role);
};

export const currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite = roles => {
  return roles.some( role => (currentlyLoggedInOrImpersonatingUserHasRoleInCurrentlySelectedSite(role)));
};

export const setCurrentlyLoggedInOrImpersonatingUser = user => {
  if (isInImpersonatingMode()) {
    store.dispatch(
      authActions.setImpersonatedUser(usersModel.apiToStore(user), false)
    );
  } else {
    store.dispatch(
      authActions.setLoggedInUser(usersModel.apiToStore(user))
    );
  }
};

export const updateCurrentlyLoggedInAndImpersonatingUserIfNeeded = user => {
  const { app } = store.getState();
  const transformedUser = usersModel.apiToStore(user);

  if (app.loggedInUser.id === user.id) {
    store.dispatch(
      authActions.setLoggedInUser(transformedUser)
    );
  }

  if (app.impersonating && app.impersonating.id === user.id) {
    store.dispatch(
      authActions.setImpersonatedUser(transformedUser, false)
    );
  }
};

export const getCurrentlyLoggedInOrImpersonatingUser = () => {
  const currentlyLoggedInUser = getCurrentlyLoggedInUser();

  if (!currentlyLoggedInUser) {
    return false;
  }

  const impersonatingUser = getImpersonatingUser();

  return impersonatingUser ? impersonatingUser : currentlyLoggedInUser;
};

export const getCurrentlyLoggedInOrImpersonatingUserAvailableSitesIds = () => {
  const user = getCurrentlyLoggedInOrImpersonatingUser();
  let availableSitesIds = new Set();

  if (user) {
    user.role_assignments.forEach(roleAssignment => availableSitesIds.add(roleAssignment.site_id));
  }

  const siteIds = Array.from(availableSitesIds.values());
  console.log( 'availableSiteIds: ', siteIds );
  return siteIds;
};

export const getUserSitesByRole = (roleAssignments, role) => roleAssignments
  .filter(roleAssignment => roleAssignment.role_name === role)
  .map(roleAssignment => roleAssignment.site_id);

export const getUserSitesByRoles = (roleAssignments, roles) => roleAssignments
  .filter(roleAssignment => roles.indexOf(roleAssignment.role_name) !== -1)
  .map(roleAssignment => roleAssignment.site_id);

export const getCurrentlyLoggedInOrImpersonatingUserSitesByRole = (role) => {
  const loggedUser = getCurrentlyLoggedInOrImpersonatingUser();

  if(!loggedUser) {
    return [];
  }

  return (loggedUser.role_assignments || [])
    .filter(roleAssignment => roleAssignment.role_name === role)
    .map(roleAssignment => roleAssignment.site_id);
};

export const isCompleteRegistrationProcessPending = () => {
  if (getCurrentlyLoggedInOrImpersonatingUser()) {
    return false;
  }

  return store.getState().app.userRegistration.idTokenPayload !== null;
};

export const getCurrentlyLoggedInUser = () => store.getState().app.loggedInUser;

export const getImpersonatingUser = () => store.getState().app.impersonating;

export const isInImpersonatingMode = () => {
  const state = store.getState();
  const activeUser = getCurrentlyLoggedInOrImpersonatingUser();

  if (!state || !state.app || !state.app.idTokenPayload || !state.app.idTokenPayload.id || !activeUser) {
    return false;
  }

  return state.app.idTokenPayload.id !== activeUser.id;
};

export const setDefaultSiteAndSites = roleAssignments => Bluebird
  .try(() => store.dispatch(sitesActions.fetchSites()))
  .then(() => {
    // the sites aren't back yet - only the fetch dispatch has completed...
    const storeSites = store.getState().entities.sites;

    // let's check if user has any roles at all to play with
    if (!roleAssignments.length) {
      return store.dispatch(appActions.switchCurrentSite(null));
    }

    // role assignments are sorted by how the role is important in the reducer, so we can rely on that here
    const firstRoleAssignmentWithSiteId = roleAssignments.find(
      roleAssignment =>
        roleAssignment.hasOwnProperty('site_id') && typeof roleAssignment.site_id === 'number'
    );
    const defaultSiteId = firstRoleAssignmentWithSiteId ? firstRoleAssignmentWithSiteId.site_id : null;

    console.log('setDefaultSiteAndSites: ', roleAssignments, defaultSiteId );

    return store.dispatch(
      appActions.switchCurrentSite(
        defaultSiteId && storeSites.byId[defaultSiteId]
          ? storeSites.byId[defaultSiteId]
          : storeSites.byId[storeSites.allIds[0]]
      )
    );
  });

export const shouldHideMainDrawer = (user, isMobile = false) => {
  const checkedUserRoles = get(user, 'role_assignments', []);
  const isRescueConfirmPage = matchPath(window.location.pathname, {
    path: routes.rescueConfirm,
    exact: true,
  });
  const isRescueReleasePage = matchPath(window.location.pathname, {
    path: routes.rescueRelease,
    exact: true,
  });

  const hasRoleAssigned = checkedUserRoles.length > 0;
  const isAdmin = hasAnyRoleInCurrentlySelectedSite(getCurrentlyLoggedInUser(), [
    Roles.Admin,
    Roles.NationalSiteDirector,
  ]);

  const isEmailChangedPage = matchPath(window.location.pathname, {
    path: routes.emailChanged,
    exact: true,
  });

  // on mobile: hide menu for all users
  // on desktop: hide menu for users without roles and on rescue confirm page
  return isMobile || (!isAdmin && !hasRoleAssigned) || isRescueConfirmPage || isRescueReleasePage || isEmailChangedPage;
};

const _getGroupedGlobalAndNonGlobalRoles = (roleAssignments, siteId) => {
  const groupedRoles = groupBy(roleAssignments, ra => ra.site_id ? ra.site_id : 'global');

  return [
    ...get(groupedRoles, 'global', []),
    ...get(groupedRoles, `[${siteId}]`, []),
  ];
};
